/**
 * Persist Reporting composer across full page refresh (per hotel + signed-in staff).
 * Hotel-only keys leaked drafts across users on the same browser — those are dropped.
 * Audit sets can be large — quota errors are ignored so the in-memory form still works.
 */

import {
  createInitialDescriptionBlocks,
  ReportDescriptionBlock,
} from './reportDescriptionBlocks';
import { ReportAuditSet, MAX_REPORT_AUDIT_SETS } from './reportAuditSets';
import {
  createDefaultReportingComposer,
  PreparedReportDraft,
  ReportingComposerState,
} from './reportingComposerState';
import { sanitizeReportContextUsers } from './reportContextUsers';
import { sanitizeAlertLevelSummary } from './reportAudits';

const STORAGE_PREFIX = 'lodgefirst_reporting_composer_v1';

/** Staff id, else username — empty when signed out so we never use a shared key. */
export const reportingComposerUserKey = (): string => {
  if (typeof localStorage === 'undefined') return '';
  return String(
    localStorage.getItem('staffId')
    || localStorage.getItem('username')
    || '',
  ).trim();
};

const storageKey = (): string | null => {
  const userKey = reportingComposerUserKey();
  if (!userKey) return null;
  const hotelId = (typeof localStorage !== 'undefined'
    && localStorage.getItem('hotel_data_id'))
    || 'default';
  return `${STORAGE_PREFIX}:${hotelId}:${userKey}`;
};

/** Remove pre-user-scoped drafts (`prefix:hotelId`) so they cannot be read by the next staff. */
const dropLegacyHotelOnlyComposers = (): void => {
  try {
    if (typeof localStorage === 'undefined') return;
    const toRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i);
      if (!key || !key.startsWith(`${STORAGE_PREFIX}:`)) continue;
      const rest = key.slice(STORAGE_PREFIX.length + 1);
      if (!rest.includes(':')) toRemove.push(key);
    }
    toRemove.forEach((key) => localStorage.removeItem(key));
  } catch {
    // ignore
  }
};

const sanitizeDescBlocks = (raw: unknown): ReportDescriptionBlock[] => {
  if (!Array.isArray(raw) || !raw.length) return createInitialDescriptionBlocks();
  const out: ReportDescriptionBlock[] = [];
  raw.forEach((item: any) => {
    if (!item || typeof item !== 'object') return;
    if (item.kind === 'text' && typeof item.id === 'string') {
      out.push({ id: item.id, kind: 'text', text: String(item.text || '') });
      return;
    }
    if (item.kind === 'log' && typeof item.id === 'string' && item.sourceId) {
      out.push({
        id: item.id,
        kind: 'log',
        sourceId: String(item.sourceId),
        tmpId: Number(item.tmpId) > 0 ? Math.floor(Number(item.tmpId)) : 1,
        message: String(item.message || ''),
        type: String(item.type || 'Audit'),
        date: item.date ? String(item.date) : undefined,
        value: item.value != null ? String(item.value) : undefined,
        units: item.units != null ? String(item.units) : undefined,
        valueMode: item.valueMode === 'units' ? 'units' : undefined,
        combinedSourceIds: Array.isArray(item.combinedSourceIds)
          ? item.combinedSourceIds.map((id: unknown) => String(id || '').trim()).filter(Boolean)
          : undefined,
      });
    }
  });
  return out.length ? out : createInitialDescriptionBlocks();
};

const sanitizeAuditSets = (
  raw: unknown,
  /** Legacy global toggle — used when a set has no showCharts field. */
  fallbackShowCharts = true,
): ReportAuditSet[] => {
  if (!Array.isArray(raw)) return [];
  return raw.slice(0, MAX_REPORT_AUDIT_SETS).map((item: any, index: number) => {
    const rowsIn = Array.isArray(item?.rows) ? item.rows : [];
    const rows = rowsIn.map((r: any, i: number) => ({
      sourceId: String(r?.sourceId || ''),
      tmpId: Number(r?.tmpId) > 0 ? Math.floor(Number(r.tmpId)) : i + 1,
      type: String(r?.type || 'Audit'),
      message: String(r?.message || ''),
      date: r?.date ? String(r.date) : undefined,
      value: r?.value != null ? String(r.value) : undefined,
      units: r?.units != null ? String(r.units) : undefined,
    })).filter((r: { sourceId: string }) => r.sourceId);
    const hasOwnShowCharts = item != null && typeof item === 'object' && 'showCharts' in item;
    return {
      id: String(item?.id || `restored_${index + 1}`),
      serial: Number(item?.serial) > 0 ? Math.floor(Number(item.serial)) : index + 1,
      rows,
      descBlocks: sanitizeDescBlocks(item?.descBlocks),
      showCharts: hasOwnShowCharts ? item.showCharts !== false : fallbackShowCharts,
      valueMode: item?.valueMode === 'units' ? 'units' as const : 'value' as const,
      chartsTitle: typeof item?.chartsTitle === 'string' ? String(item.chartsTitle).slice(0, 80) : '',
      combineLogTypes: item?.combineLogTypes === true,
      typeAttributeMode: item?.typeAttributeMode === 'sku'
        || item?.typeAttributeMode === 'inventory_group'
        || item?.typeAttributeMode === 'department'
        ? item.typeAttributeMode
        : undefined,
    };
  }).filter((s) => s.id);
};

const sanitizeDraft = (raw: unknown): PreparedReportDraft | null => {
  if (!raw || typeof raw !== 'object') return null;
  const d = raw as any;
  if (!d.id) return null;
  return {
    id: String(d.id),
    viewUrl: d.viewUrl != null ? String(d.viewUrl) : null,
    preview: d.preview != null ? String(d.preview) : undefined,
  };
};

/** Normalize a parsed blob into a safe composer snapshot. */
export const sanitizeReportingComposer = (raw: unknown): ReportingComposerState => {
  const base = createDefaultReportingComposer();
  if (!raw || typeof raw !== 'object') return base;
  const c = raw as any;
  // Older composers stored one global toggle — seed sets that lack showCharts.
  const legacyShowCharts = c.showCharts !== false;
  const auditSets = sanitizeAuditSets(c.auditSets, legacyShowCharts);
  return {
    toEmail: typeof c.toEmail === 'string' ? c.toEmail : '',
    subject: typeof c.subject === 'string' ? c.subject.slice(0, 120) : '',
    descBlocks: sanitizeDescBlocks(c.descBlocks),
    activeTextBlockId: typeof c.activeTextBlockId === 'string' ? c.activeTextBlockId : '',
    includeAudits: auditSets.length > 0 || Boolean(c.includeAudits),
    auditSets,
    showCharts: legacyShowCharts,
    chartsMetricMode: 'balances',
    previewPage: Number(c.previewPage) > 0 ? Math.floor(Number(c.previewPage)) : 1,
    draft: sanitizeDraft(c.draft),
    replyToReportId: typeof c.replyToReportId === 'string' && c.replyToReportId
      ? String(c.replyToReportId)
      : null,
    replyToFromName: typeof c.replyToFromName === 'string' ? String(c.replyToFromName) : '',
    composerPhase: c.composerPhase === 'body' ? 'body' : 'details',
    contextUsers: sanitizeReportContextUsers(c.contextUsers),
    alertLevelSummary: typeof c.alertLevelSummary === 'string'
      ? sanitizeAlertLevelSummary(c.alertLevelSummary)
      : '',
  };
};

export const loadReportingComposer = (): ReportingComposerState => {
  try {
    dropLegacyHotelOnlyComposers();
    if (typeof localStorage === 'undefined') return createDefaultReportingComposer();
    const key = storageKey();
    if (!key) return createDefaultReportingComposer();
    const raw = localStorage.getItem(key);
    if (!raw) return createDefaultReportingComposer();
    return sanitizeReportingComposer(JSON.parse(raw));
  } catch {
    return createDefaultReportingComposer();
  }
};

export const saveReportingComposer = (composer: ReportingComposerState): void => {
  try {
    dropLegacyHotelOnlyComposers();
    if (typeof localStorage === 'undefined') return;
    const key = storageKey();
    // Signed out — do not write a hotel-wide blob the next user would inherit.
    if (!key) return;
    localStorage.setItem(key, JSON.stringify(composer));
  } catch {
    // Quota / private mode — leave memory state; next refresh may miss large audit sets.
  }
};

export const clearReportingComposerStorage = (): void => {
  try {
    if (typeof localStorage === 'undefined') return;
    const key = storageKey();
    if (!key) return;
    localStorage.removeItem(key);
  } catch {
    // ignore
  }
};
